import { QueryClient } from "react-query";

/**
 * Custom header required on every mutating request (CSRF belt-and-suspenders — see
 * `requireCsrfHeader` in `packages/api-common`'s doc comment for why: `SameSite=Lax` on the
 * session cookie already blocks it riding along on a cross-site POST/PUT/PATCH/DELETE in modern
 * browsers, this is defense-in-depth for older ones). Matches the header name used by the
 * parallel manager-app cookie-auth migration (Phase 1) for consistency.
 */
const CSRF_HEADER_NAME = "X-Requested-With";
const CSRF_HEADER_VALUE = "XMLHttpRequest";

/**
 * Dispatched on `window` for ANY 401, from ANY request (initial load with no session yet, or a
 * session that was fine and just died mid-use — Addison: those are the same situation from the
 * user's side, treat them the same). `useInterfaceAccess` listens for this (see its own doc
 * comment) and folds it into the same "denied: unauthenticated" branch its own `customer-context`
 * query already produces on a direct 401 — so every interface's placeholder chrome + centered
 * `InterfaceAccessNotice` handles this, not a full-page swap. `CustomerApiService` is a static
 * class with no Router/React access, so dispatching a DOM event — rather than hard-redirecting
 * like vault's `ApiService` does (`window.location.assign("/ui/401")`) — is the plug-in point the
 * spec (§2.3) describes: "sets an expired-session flag in React context (or redirects to /expired
 * route)". (Used to gate this on a `sessionEverConfirmed` flag and only fire for a GENUINE
 * mid-session expiry, swapping in a full-screen `<ExpiredTokenPage>` — removed, see git history,
 * once both cases got the same treatment there was nothing left for the flag to distinguish.)
 */
export const SESSION_EXPIRED_EVENT = "customer-session-expired";

/**
 * Structured error thrown by `CustomerApiService.fetch` for any non-ok response (401 included —
 * see `handleResponse` below).
 *
 * ROUND 17: replaces the old behavior of throwing a bare `Error` whose `.message` was the RAW
 * response text — customer-api's error handler (`apps/customer-api/src/loaders/express.ts`)
 * actually returns a structured `{ error: { name, message, ...extra } }` JSON body (see that
 * file's "RESPONSE CONTRACT" doc comment), but this client was never parsing it, so every caller
 * either showed a generic "could not load" banner or (`CandidateAssignmentsScreen.tsx`'s
 * `setSaveError`) accidentally rendered the raw JSON blob as if it were plain text. `code` exposes
 * `extra.code` when the server sets one (e.g. `spotter/tier.ts`'s `SPOTTER_ACCESS_DENIED_CODE`) so
 * a screen can key off a SPECIFIC failure reason instead of pattern-matching `.message` text.
 */
export class ApiError extends Error {
  public status: number;
  public code?: string;
  constructor(message: string, status: number, code?: string, name?: string) {
    super(message);
    this.name = name || "ApiError";
    this.status = status;
    this.code = code;
  }
}

/**
 * `defaultOptions.queries.retry` for the shared `queryClient` below — pulled out standalone so it
 * can be unit-tested without spinning up a real `QueryClient`. Replaces react-query v3's own
 * default of a blind `retry: 3` with exponential backoff: a 401/403 will never succeed on retry
 * (401 already handled via `useInterfaceAccess`'s denied state; 403 is a real permission denial from
 * `requireSpotterAccess`/similar route guards), so retrying either just delays the already-correct
 * error banner by up to ~7s per query — worse when several fire in parallel (e.g.
 * `FieldsScreen`'s 6 queries). Every other error (network blip, 5xx) still gets the normal 3
 * retries with default backoff — `failureCount < 3` is react-query's own default numeric-retry
 * check (see its `retryer.js`), not a new/different threshold.
 *
 * Queries that run through the lifted `CollectionBrowser`/`MetadataViewer`/`JobViewer` trees reach
 * this `queryClient` too, but see a `FetchError` instead of an `ApiError` — `customerAppsHostAdapter
 * .api.fetch` (Monday S2.5a) translates to that shape, not this one, since it's the shape
 * `item-browser/fileActions.ts` already duck-types on. Checked structurally (`isFetchError` +
 * `status`), the same way `fileActions.ts` itself checks it, rather than importing that class here
 * — this file is app-core and shouldn't gain a dependency on one feature folder's error shape.
 */
export function shouldRetryQuery(
  failureCount: number,
  error: unknown
): boolean {
  const isAuthFailure =
    (error instanceof ApiError &&
      (error.status === 401 || error.status === 403)) ||
    (!!error &&
      typeof error === "object" &&
      "isFetchError" in error &&
      "status" in error &&
      ((error as { status: unknown }).status === 401 ||
        (error as { status: unknown }).status === 403));
  if (isAuthFailure) {
    return false;
  }
  return failureCount < 3;
}

/**
 * Customer stack API client.
 *
 * COOKIE-AUTH CUTOVER (Monday 12656019094, full cutover): auth used to be a raw iconik token kept
 * in `localStorage` and sent as an `Auth-Token` header on every request. It's now an httpOnly
 * `customer_session` cookie, set once by `POST /session/exchange` (see `exchangeToken` below,
 * called from `index.tsx`'s boot sequence) and attached automatically by the browser on every
 * subsequent request via `credentials: "include"` — this class never reads/writes/holds the raw
 * token itself anymore (it isn't readable from JS at all, by design).
 *
 * Diverges from vault-frontend's `ApiService` in the ways the customer-portal spec calls out
 * (§2.1/§2.3/§6.2.1):
 *   - On 401 this does NOT hard-redirect. It always dispatches `SESSION_EXPIRED_EVENT` (there is
 *     no client-side token left to clear) and throws an `ApiError`, and every interface's
 *     `useInterfaceAccess` handles that itself, in place — no session yet and a session that died
 *     mid-use get the identical placeholder-chrome + centered-notice treatment.
 *   - Responses are still BARE payloads (no `{ data }` envelope) — same as vault.
 *   - Errors throw the structured `ApiError` above (status + optional `code`), parsed from
 *     customer-api's real `{ error: { name, message, ...extra } }` JSON contract.
 *
 * `fetch` also awaits `bootExchange` (if any) before sending a request — see that field's doc
 * comment. This is the one chokepoint every screen's data-fetching already routes through, so it
 * covers every current and future query/mutation without threading an `enabled` flag through each
 * one individually.
 */
export default class CustomerApiService {
  static apiUrl = process.env.REACT_APP_API_URL ?? "";

  /** Shared client for every `useQuery` in the app — see `shouldRetryQuery` above for the default
   *  `retry` policy every query gets unless it opts out/overrides. */
  static queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        retry: shouldRetryQuery,
      },
    },
  });

  /**
   * The boot-time `/session/exchange` call, if `index.tsx` kicked one off (i.e. the page loaded
   * with `?authorization_token=`). `fetch` awaits this before every request so the app's first
   * real API call can never race the Set-Cookie response, even though `index.tsx` now mounts the
   * React tree immediately instead of waiting for the exchange itself. Settled (never rejects) —
   * a failed exchange just means no cookie got set, which the normal 401 handling below already
   * covers. `null` on every other boot path (plain refresh, no token), so `fetch` adds zero
   * overhead there.
   */
  private static bootExchange: Promise<void> | null = null;

  /** Called once from `index.tsx` when a launch token is present in the URL. */
  public static beginBootExchange(token: string): void {
    this.bootExchange = this.exchangeToken(token).catch(() => undefined);
  }

  /**
   * `apiUrl` always carries a `/api` suffix (`REACT_APP_API_URL`, e.g.
   * `https://iconik-api-staging.cinesys.io/api`) — every OTHER path passed to `fetch()` below is
   * relative to that (`/context`, `/spotter/mapping`, ...), matching `apps/customer-api`'s own
   * `/api/*` route mount. `/session/exchange` is deliberately mounted OUTSIDE `/api` on the
   * backend (see `routes/session.ts`'s doc comment — it's the auth bootstrap, not gated by
   * `requireToken`), so it needs the `/api` suffix stripped back off, not appended to. Building
   * `${apiUrl}/session/exchange` directly would produce `.../api/session/exchange`, a path the
   * backend never mounts anything at.
   */
  private static get rootUrl(): string {
    return this.apiUrl.replace(/\/api\/?$/, "");
  }

  /**
   * `POST /session/exchange` — the auth bootstrap itself (see
   * `apps/customer-api/src/routes/session.ts`'s doc comment). Called once, before the App tree
   * ever mounts (`index.tsx`), with the raw `?authorization_token=` iconik gave us. Throws on a
   * non-2xx response (invalid/expired launch token) — the caller decides what to do with that;
   * this method has no app-level state to react with.
   */
  public static async exchangeToken(token: string): Promise<void> {
    const response = await fetch(`${this.rootUrl}/session/exchange`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        [CSRF_HEADER_NAME]: CSRF_HEADER_VALUE,
      },
      body: JSON.stringify({ token }),
    });
    if (!response.ok) {
      throw new Error(`[${response.status}] session exchange failed`);
    }
  }

  /**
   * Make a request to customer-api. Returns the BARE parsed JSON body (no
   * envelope unwrap) — same contract as vault's ApiService.
   */
  public static async fetch<T, B = unknown>(
    method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
    path: string,
    body?: B
  ): Promise<T> {
    if (this.bootExchange) {
      await this.bootExchange;
    }

    const request: RequestInit = {
      method,
      credentials: "include",
      headers: this.getHeaders(method),
      body: method !== "GET" ? JSON.stringify(body) : undefined,
    };

    const response = await fetch(this.apiUrl + path, request);

    return this.handleResponse<T>(response);
  }

  /** No auth header at all — the session cookie auto-attaches (`credentials: "include"`). The
   *  CSRF header only goes on mutating methods; a GET can't be a CSRF target. */
  private static getHeaders(method: string): Record<string, string> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (method !== "GET") {
      headers[CSRF_HEADER_NAME] = CSRF_HEADER_VALUE;
    }
    return headers;
  }

  private static async handleResponse<T>(response: Response): Promise<T> {
    if (response.status === 401) {
      // Every 401 dispatches this — see `SESSION_EXPIRED_EVENT`'s doc comment above for why no
      // session yet and a session that died mid-use get the same treatment. Nothing to clear
      // client-side either way; the cookie is httpOnly and a fresh sign-in at iconik just
      // overwrites it. Thrown as an `ApiError` (not a bare `Error`) so the shared `queryClient`'s
      // default `retry` can key off `.status` and skip the pointless retries below.
      window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
      throw new ApiError("Unauthorized", 401);
    }

    if (response.ok) {
      // BARE payload: return the parsed body directly, no { data } unwrap.
      // Tolerate empty bodies (e.g. 204) to stay type-correct.
      const text = await response.text();
      return (text ? JSON.parse(text) : undefined) as T;
    }

    // customer-api's real error contract is structured JSON — `{ error: { name, message,
    // ...extra } }` (see `apps/customer-api/src/loaders/express.ts`'s "RESPONSE CONTRACT" doc
    // comment). Parse it into an `ApiError`, keeping its `name`/`code` too. A proxy error (502/504)
    // comes back as an HTML page instead — keep the status but drop the raw HTML rather than
    // surfacing it as the error message.
    const text = await response.text();
    let message: string;
    let code: string | undefined;
    let name: string | undefined;
    try {
      const parsed = JSON.parse(text) as {
        error?: string | { name?: string; message?: string; code?: string };
      };
      // Most routes send `{ error: { name, message } }`, but a handful still send a bare
      // `{ error: "<string>" }` -- keep that string as the message instead of collapsing it to
      // the generic fallback.
      const errorMessage =
        typeof parsed?.error === "string"
          ? parsed.error
          : parsed?.error?.message;
      message = errorMessage || `[${response.status}] Request failed`;
      if (typeof parsed?.error === "object") {
        if (typeof parsed.error.code === "string") {
          code = parsed.error.code;
        }
        if (typeof parsed.error.name === "string") {
          name = parsed.error.name;
        }
      }
    } catch {
      message = "";
    }
    throw new ApiError(message, response.status, code, name);
  }
}
